import React, { useEffect, useState } from "react";
import {
    Table,
    Spinner,
    Alert,
    Badge,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Collapse,
    Row,
    Col,
} from "reactstrap";
import {
    Carousel,
    CarouselItem,
    CarouselControl,
    CarouselIndicators,
    CarouselCaption,
} from "reactstrap";
import { enqueueSnackbar } from 'notistack';
import { getAssetsByValuation } from "../../../../services/ValitanApiService";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../../utils/exportUtils";
import { resolveAssetNameDisplay } from "../../../../utils/otherValueDisplay";
import logo from "../../../../assets/images/logo.png";
import "./AssetsTab.css";

const BRAND_BLUE = "#0147C8";

const AssetsTab = ({ valuationId, assets: assetsProp = [], loading: loadingProp = false }) => {
    const [assets, setAssets] = useState(assetsProp);
    const [loading, setLoading] = useState(loadingProp || (!assetsProp.length && !!valuationId));
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [columnModalOpen, setColumnModalOpen] = useState(false);
    const [pendingExportType, setPendingExportType] = useState(null);

    // Modal & carousel state
    const [selectedPhotos] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);

    const toggleRow = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    // If assets are passed as props, use them directly
    useEffect(() => {
        if (assetsProp && assetsProp.length > 0) {
            setAssets(assetsProp);
            setLoading(false);
        }
    }, [assetsProp]);
    
    // Sync loading state from parent
    useEffect(() => {
        if (loadingProp !== undefined) {
            setLoading(loadingProp);
        }
    }, [loadingProp]);

    // If valuationId is provided, fetch assets
    useEffect(() => {
        if (!valuationId) return;

        const fetchAssets = async () => {
            try {
                setLoading(true);
                const response = await getAssetsByValuation(valuationId);

                if (response.status === 200 && response.data) {
                    setAssets(response.data.data || []);
                } else {
                    setAssets([]);
                }
            } catch (error) {
                console.error("❌ Failed to fetch assets:", error);
                setAssets([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAssets();
    }, [valuationId]);

    const showHasSiteWorkColumn =
        assets.length > 0 &&
        assets.some((asset) => String(asset?.asset_category?.code || asset?.category_code || "").toUpperCase() === "BLDG");

    const next = () => {
        if (animating) return;
        if (activeIndex < selectedPhotos.length - 1) {
            setActiveIndex(activeIndex + 1);
        }
    };

    const previous = () => {
        if (animating) return;
        if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }
    };

    const goToIndex = (newIndex) => {
        if (animating) return;
        setActiveIndex(newIndex);
    };

    const formatCaption = (text) => {
        if (!text) return "";
        return text
            .toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Get export headers - same as "all" category
    const getExportHeaders = () => {
        const commonFields = [
            { key: "serialNumber", label: "#", defaultSelected: true },
            { key: "category_name", label: "Category", defaultSelected: true },
            { key: "barcode", label: "Barcode", defaultSelected: true },
            { key: "name", label: "Name", defaultSelected: true },
            { key: "other_name", label: "Other Name", defaultSelected: false },
            { key: "description", label: "Description", defaultSelected: true },
            { key: "quantity", label: "Quantity", defaultSelected: true },
            { key: "serial_number", label: "Serial Number", defaultSelected: false },
            { key: "observed_condition_name", label: "Condition", defaultSelected: true },
            { key: "observed_condition", label: "Condition (Raw)", defaultSelected: false },
            { key: "remark", label: "Remark", defaultSelected: true },
            { key: "acquisition_date", label: "Acquisition Date", defaultSelected: false },
            { key: "useful_life_span_years", label: "Useful Life Span (Years)", defaultSelected: false },
        ];

        const locationFields = [
            { key: "ward", label: "Ward", defaultSelected: false },
            { key: "institution_name", label: "Institution Name", defaultSelected: false },
            { key: "institution_category", label: "Institution Category", defaultSelected: false },
            { key: "institution_category_other", label: "Institution Category Other", defaultSelected: false },
            { key: "institution_other", label: "Institution Other", defaultSelected: false },
            { key: "latitude", label: "Latitude", defaultSelected: false },
            { key: "longitude", label: "Longitude", defaultSelected: false },
        ];

        const buildingFields = [
            { key: "building_name", label: "Building Name", defaultSelected: false },
            { key: "other_building_name", label: "Other Building Name", defaultSelected: false },
            { key: "building_number", label: "Building Number", defaultSelected: false },
            { key: "floor", label: "Floor", defaultSelected: false },
            { key: "room_name_or_number", label: "Room Name/Number", defaultSelected: false },
            { key: "room_others", label: "Room Others", defaultSelected: false },
            { key: "number_of_buildings", label: "Number of Buildings", defaultSelected: false },
            { key: "building_use", label: "Building Use", defaultSelected: false },
        ];

        const landFields = [
            { key: "plot_number", label: "Plot Number", defaultSelected: false },
            { key: "plot_size", label: "Plot Size", defaultSelected: false },
            { key: "size_unit", label: "Size Unit", defaultSelected: false },
            { key: "block_number", label: "Block Number", defaultSelected: false },
            { key: "certificate_of_title", label: "Certificate of Title", defaultSelected: false },
            { key: "tenure_years", label: "Tenure Years", defaultSelected: false },
            { key: "rate_per_unit", label: "Rate Per Unit", defaultSelected: false },
            { key: "is_land_surveyed", label: "Is Land Surveyed", defaultSelected: false },
            { key: "registration_number", label: "Registration Number", defaultSelected: false },
            { key: "right_of_occupance_start", label: "Right of Occupancy Start", defaultSelected: false },
            { key: "right_of_occupance_end", label: "Right of Occupancy End", defaultSelected: false },
        ];

        const vehicleFields = [
            { key: "registration_number", label: "Registration Number", defaultSelected: false },
            { key: "chassis_number", label: "Chassis Number", defaultSelected: false },
            { key: "engine_number", label: "Engine Number", defaultSelected: false },
            { key: "make", label: "Make", defaultSelected: false },
            { key: "model", label: "Model", defaultSelected: false },
            { key: "year_of_manufacture", label: "Year of Manufacture", defaultSelected: false },
            { key: "engine_capacity", label: "Engine Capacity (cc)", defaultSelected: false },
            { key: "fuel_type", label: "Fuel Type", defaultSelected: false },
            { key: "imported_from", label: "Imported From", defaultSelected: false },
            { key: "imported_from_others", label: "Imported From Others", defaultSelected: false },
            { key: "body_type", label: "Body Type", defaultSelected: false },
            { key: "body_type_others", label: "Body Type Others", defaultSelected: false },
        ];

        const utilityFields = [
            ...(showHasSiteWorkColumn ? [{ key: "has_site_work", label: "Has Site Work", defaultSelected: false }] : []),
            { key: "has_water", label: "Has Water", defaultSelected: false },
            { key: "has_electricity", label: "Has Electricity", defaultSelected: false },
            { key: "utilities", label: "Utilities (Combined)", defaultSelected: false },
        ];

        const measurementFields = [
            { key: "measurement", label: "Measurement", defaultSelected: false },
            { key: "measurement_unit", label: "Measurement Unit", defaultSelected: false },
        ];

        const financialFields = [
            { key: "current_replacement_cost", label: "Current Replacement Cost", defaultSelected: false },
            { key: "historical_cost", label: "Historical Cost", defaultSelected: false },
            { key: "accumulated_depreciation_amount", label: "Accumulated Depreciation", defaultSelected: false },
            { key: "fair_value", label: "Fair Value", defaultSelected: false },
            { key: "net_book_value", label: "Net Book Value", defaultSelected: false },
            { key: "remaining_useful_life_years", label: "Remaining Useful Life (Years)", defaultSelected: false },
            { key: "cost_opening_balance", label: "Cost Opening Balance", defaultSelected: false },
            { key: "cost_additions", label: "Cost Additions", defaultSelected: false },
            { key: "cost_disposals", label: "Cost Disposals", defaultSelected: false },
            { key: "cost_closing_balance", label: "Cost Closing Balance", defaultSelected: false },
            { key: "depreciation_opening_balance", label: "Depreciation Opening Balance", defaultSelected: false },
            { key: "depreciation_charge", label: "Depreciation Charge", defaultSelected: false },
            { key: "depreciation_disposals", label: "Depreciation Disposals", defaultSelected: false },
            { key: "depreciation_closing_balance", label: "Depreciation Closing Balance", defaultSelected: false },
            { key: "monthly_depreciation", label: "Monthly Depreciation", defaultSelected: false },
        ];

        const valuationFields = [
            { key: "client_name", label: "Client Name", defaultSelected: false },
            { key: "project_name", label: "Project Name", defaultSelected: false },
            { key: "valuer_name", label: "Valuer", defaultSelected: false },
        ];

        const auditFields = [
            { key: "created_at", label: "Created At", defaultSelected: false },
            { key: "updated_at", label: "Updated At", defaultSelected: false },
        ];

        return [
            ...commonFields,
            ...locationFields,
            ...buildingFields,
            ...landFields,
            ...vehicleFields,
            ...utilityFields,
            ...measurementFields,
            ...financialFields,
            ...valuationFields,
            ...auditFields,
        ];
    };

    // Open column selector modal
    const openColumnSelector = (exportType) => {
        if (assets.length === 0) {
            enqueueSnackbar(
                'No assets found to export for this valuation.',
                {
                    variant: 'warning',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
            return;
        }
        setPendingExportType(exportType);
        setColumnModalOpen(true);
    };

    // Perform export with selected columns
    const performExport = () => {
        const allHeaders = getExportHeaders();

        // Read selected columns from DOM checkboxes
        const checkboxNodes = document.querySelectorAll('input[name="export-column"]');
        const selectedKeys = [];
        checkboxNodes.forEach((node, index) => {
            if (node.checked) {
                const header = allHeaders[index];
                if (header) {
                    selectedKeys.push(header.key);
                }
            }
        });

        const headers = allHeaders.filter((h) => selectedKeys.includes(h.key));

        if (headers.length === 0) {
            enqueueSnackbar(
                'Please select at least one column to export.',
                {
                    variant: 'info',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
            return;
        }

        // Process assets data
        const processedAssets = assets.map((asset, index) => {
            const processed = { ...asset };
            
            processed.serialNumber = index + 1;
            
            if (selectedKeys.includes('utilities')) {
                const utilities = [];
                if (asset.has_water) utilities.push('Water');
                if (asset.has_electricity) utilities.push('Electricity');
                processed.utilities = utilities.length > 0 ? utilities.join(', ') : '-';
            }
            
            if (selectedKeys.includes('category_name')) {
                processed.category_name = asset.asset_category?.name || asset.category_name || '-';
            }
            
            if (selectedKeys.includes('building_use')) {
                processed.building_use = asset.building_use?.name || asset.building_use || '-';
            }
            
            if (selectedKeys.includes('ward')) {
                processed.ward = asset.ward?.name || asset.ward || '-';
            }
            
            if (selectedKeys.includes('has_water')) {
                processed.has_water = asset.has_water ? 'Yes' : 'No';
            }
            if (selectedKeys.includes('has_site_work')) {
                processed.has_site_work = asset.has_site_work || asset.hasSiteWork ? 'Yes' : 'No';
            }
            if (selectedKeys.includes('has_electricity')) {
                processed.has_electricity = asset.has_electricity ? 'Yes' : 'No';
            }
            if (selectedKeys.includes('is_land_surveyed')) {
                processed.is_land_surveyed = asset.is_land_surveyed ? 'Yes' : 'No';
            }
            
            if (selectedKeys.includes('acquisition_date') && asset.acquisition_date) {
                try {
                    processed.acquisition_date = new Date(asset.acquisition_date).toLocaleDateString();
                } catch (e) {
                    processed.acquisition_date = asset.acquisition_date;
                }
            }
            if (selectedKeys.includes('right_of_occupance_start') && asset.right_of_occupance_start) {
                try {
                    processed.right_of_occupance_start = new Date(asset.right_of_occupance_start).toLocaleDateString();
                } catch (e) {
                    processed.right_of_occupance_start = asset.right_of_occupance_start;
                }
            }
            if (selectedKeys.includes('right_of_occupance_end') && asset.right_of_occupance_end) {
                try {
                    processed.right_of_occupance_end = new Date(asset.right_of_occupance_end).toLocaleDateString();
                } catch (e) {
                    processed.right_of_occupance_end = asset.right_of_occupance_end;
                }
            }
            if (selectedKeys.includes('created_at') && asset.created_at) {
                try {
                    processed.created_at = new Date(asset.created_at).toLocaleString();
                } catch (e) {
                    processed.created_at = asset.created_at;
                }
            }
            if (selectedKeys.includes('updated_at') && asset.updated_at) {
                try {
                    processed.updated_at = new Date(asset.updated_at).toLocaleString();
                } catch (e) {
                    processed.updated_at = asset.updated_at;
                }
            }
            
            if (selectedKeys.includes('plot_size') && asset.plot_size) {
                processed.plot_size = typeof asset.plot_size === 'number' 
                    ? asset.plot_size.toLocaleString() 
                    : asset.plot_size;
            }
            if (selectedKeys.includes('rate_per_unit') && asset.rate_per_unit) {
                processed.rate_per_unit = typeof asset.rate_per_unit === 'number' 
                    ? asset.rate_per_unit.toLocaleString() 
                    : asset.rate_per_unit;
            }
            if (selectedKeys.includes('latitude') && asset.latitude) {
                processed.latitude = typeof asset.latitude === 'number' 
                    ? asset.latitude.toString() 
                    : asset.latitude;
            }
            if (selectedKeys.includes('longitude') && asset.longitude) {
                processed.longitude = typeof asset.longitude === 'number' 
                    ? asset.longitude.toString() 
                    : asset.longitude;
            }
            
            return processed;
        });

        const filename = `assets_${new Date().toISOString().split("T")[0]}`;
        
        if (pendingExportType === 'csv') {
            exportToCSV(processedAssets, headers, `${filename}.csv`);
        } else if (pendingExportType === 'excel') {
            exportToExcel(processedAssets, headers, `${filename}.xlsx`);
        } else if (pendingExportType === 'pdf') {
            let exportedBy = "System User";
            try {
                const authUser = localStorage.getItem("authUser");
                if (authUser) {
                    const { user } = JSON.parse(authUser);
                    if (user) {
                        const firstName = user.first_name || "";
                        const lastName = user.last_name || "";
                        exportedBy = `${firstName} ${lastName}`.trim() || user.username || user.email || "System User";
                    }
                }
            } catch (error) {
                console.warn("Could not get user info:", error);
            }

            let clientName = null;
            let projectName = null;
            
            if (assets.length > 0) {
                const firstAsset = assets[0];
                if (firstAsset.valuation) {
                    if (firstAsset.valuation.client) {
                        clientName = firstAsset.valuation.client.name || firstAsset.valuation.client_name || null;
                    }
                    if (firstAsset.valuation.project) {
                        projectName = firstAsset.valuation.project.name || firstAsset.valuation.project_name || null;
                    }
                }
                if (!clientName && firstAsset.client_name) {
                    clientName = firstAsset.client_name;
                }
                if (!projectName && firstAsset.project_name) {
                    projectName = firstAsset.project_name;
                }
            }

            const title = "Assets Report";
            const metadata = {
                logoPath: logo,
                exportedBy: exportedBy,
                clientName: clientName,
                projectName: projectName,
            };
            
            exportToPDF(processedAssets, headers, `${filename}.pdf`, title, metadata);
        }
        
        setColumnModalOpen(false);
    };

    // Select/Deselect all columns
    const toggleAllColumns = () => {
        const checkboxNodes = document.querySelectorAll('input[name="export-column"]');
        const allSelected = checkboxNodes.length > 0 && Array.from(checkboxNodes).every((node) => node.checked);
        Array.from(checkboxNodes).forEach((node) => {
            node.checked = !allSelected;
        });
    };

    // --- Loading & Empty States ---
    if (loading) {
        return (
            <div className="text-center my-3">
                <Spinner color="primary" />
            </div>
        );
    }

    if (!assets.length) {
        return <Alert color="info">No assets available for this valuation</Alert>;
    }

    // --- Table View ---
    return (
        <div>
            <h5 className="mb-3 text-primary fw-bold">
                <i className="ri-archive-drawer-line me-2 text-primary" /> Assets
            </h5>
            <div className="d-flex gap-2 mb-3">
                <Button color="primary" size="sm" onClick={() => openColumnSelector("csv")}>
                    Export CSV
                </Button>
                <Button color="success" size="sm" onClick={() => openColumnSelector("excel")}>
                    Export Excel
                </Button>
                <Button color="danger" size="sm" onClick={() => openColumnSelector("pdf")}>
                    Export PDF
                </Button>
            </div>

            <Table bordered hover responsive>
                <thead className="table-light">
                    <tr>
                        <th style={{ width: "50px" }}></th>
                        <th style={{ width: "50px" }}>#</th>
                        <th>Category</th>
                        <th>Barcode</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Location</th>
                        <th>Valuer</th>
                        <th>Condition</th>
                        <th>Acquisition Date</th>
                        {showHasSiteWorkColumn && <th>Has Site Work</th>}
                        <th>Utilities</th>
                        <th>Building / Plot</th>
                        <th>Remark</th>
                    </tr>
                </thead>
                <tbody>
                    {assets.map((asset, index) => {
                        const category = asset.asset_category || {};
                        const isExpanded = expandedRows.has(asset.id);

                        // Derive valuer name from related valuation (if present)
                        const valuation = asset.asset_valuation || asset.assetValuation || asset.valuation;
                        const valuer = valuation?.valuer_user || valuation?.valuerUser;
                        const valuerName =
                            asset.valuer_name || asset.valuerName ||
                            (valuer
                                ? [
                                      valuer.first_name || valuer.firstName || "",
                                      valuer.middle_name || valuer.middleName || "",
                                      valuer.last_name || valuer.lastName || "",
                                  ]
                                      .filter(Boolean)
                                      .join(" ")
                                : "-");

                        return (
                            <React.Fragment key={asset.id}>
                                <tr>
                                    <td>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => toggleRow(asset.id)}
                                            className="p-0"
                                        >
                                            <i className={`mdi ${isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                                        </Button>
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>
                                        {category.name || "-"}{" "}
                                        {category.code && (
                                            <Badge color="light" className="ms-1 border">
                                                {category.code}
                                            </Badge>
                                        )}
                                    </td>
                                    <td>{asset.barcode || "-"}</td>
                                    <td>{resolveAssetNameDisplay(asset)}</td>
                                    <td>{asset.description || "-"}</td>
                                    <td>{asset.quantity ?? "-"}</td>
                                    <td>{asset.location || "-"}</td>
                                    <td>{valuerName || "-"}</td>
                                    <td>
                                        <Badge
                                            color={
                                                (asset.observed_condition_name || asset.observed_condition || "")
                                                    .toLowerCase() === "new"
                                                    ? "success"
                                                    : "secondary"
                                            }
                                        >
                                            {asset.observed_condition_name || asset.observed_condition || "-"}
                                        </Badge>
                                    </td>
                                    <td>
                                        {asset.acquisition_date
                                            ? new Date(asset.acquisition_date).toLocaleDateString()
                                            : "-"}
                                    </td>
                                    {showHasSiteWorkColumn && (
                                        <td>
                                            {String(asset?.asset_category?.code || asset?.category_code || "").toUpperCase() === "BLDG"
                                                ? (asset.has_site_work || asset.hasSiteWork ? "Yes" : "No")
                                                : "-"}
                                        </td>
                                    )}
                                    <td>
                                        {asset.has_water && (
                                            <Badge color="info" className="me-1">
                                                Water
                                            </Badge>
                                        )}
                                        {asset.has_electricity && (
                                            <Badge color="warning" className="me-1 text-dark">
                                                Electricity
                                            </Badge>
                                        )}
                                        {!asset.has_water && !asset.has_electricity && "-"}
                                    </td>
                                    <td>
                                        {asset.building_number && (
                                            <div>
                                                <small>
                                                    <strong>Building No:</strong> {asset.building_number}
                                                </small>
                                            </div>
                                        )}
                                        {asset.plot_number && (
                                            <div>
                                                <small>
                                                    <strong>Plot No:</strong> {asset.plot_number}
                                                </small>
                                            </div>
                                        )}
                                        {asset.latitude && asset.longitude && (
                                            <div>
                                                <small>
                                                    <strong>Coords:</strong>{" "}
                                                    {asset.latitude.toFixed(5)}, {asset.longitude.toFixed(5)}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                    <td>{asset.remark || "-"}</td>
                                </tr>
                                {isExpanded && (
                                    <tr>
                                        <td colSpan="13" className="p-0">
                                            <Collapse isOpen={isExpanded}>
                                                <div className="p-3 bg-light">
                                                    <div className="row">
                                                        {/* Basic Information */}
                                                        <div className="col-md-6 mb-3">
                                                            <h6 className="text-primary border-bottom pb-2 mb-2">Basic Information</h6>
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="fw-bold" style={{ width: "40%" }}>Serial Number:</td>
                                                                        <td>{asset.serial_number || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Other Name:</td>
                                                                        <td>{asset.other_name || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Useful Life Span:</td>
                                                                        <td>{asset.useful_life_span_years ? `${asset.useful_life_span_years} years` : "-"}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Location & Institution */}
                                                        <div className="col-md-6 mb-3">
                                                            <h6 className="text-primary border-bottom pb-2 mb-2">Location & Institution</h6>
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="fw-bold" style={{ width: "40%" }}>Ward:</td>
                                                                        <td>{asset.ward?.name || asset.ward || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Institution Name:</td>
                                                                        <td>{asset.institution_name || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Institution Category:</td>
                                                                        <td>{asset.institution_category || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Institution Category Other:</td>
                                                                        <td>{asset.institution_category_other || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Institution Other:</td>
                                                                        <td>{asset.institution_other || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Latitude:</td>
                                                                        <td>{asset.latitude || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Longitude:</td>
                                                                        <td>{asset.longitude || "-"}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Building Details */}
                                                        <div className="col-md-6 mb-3">
                                                            <h6 className="text-primary border-bottom pb-2 mb-2">Building Details</h6>
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="fw-bold" style={{ width: "40%" }}>Building Name:</td>
                                                                        <td>{asset.building_name || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Other Building Name:</td>
                                                                        <td>{asset.other_building_name || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Building Number:</td>
                                                                        <td>{asset.building_number || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Number of Buildings:</td>
                                                                        <td>{asset.number_of_buildings || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Floor:</td>
                                                                        <td>{asset.floor || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Room Name/Number:</td>
                                                                        <td>{asset.room_name_or_number || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Room Others:</td>
                                                                        <td>{asset.room_others || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Building Use:</td>
                                                                        <td>{asset.building_use?.name || asset.building_use || "-"}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Land Details */}
                                                        <div className="col-md-6 mb-3">
                                                            <h6 className="text-primary border-bottom pb-2 mb-2">Land Details</h6>
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="fw-bold" style={{ width: "40%" }}>Plot Number:</td>
                                                                        <td>{asset.plot_number || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Plot Size:</td>
                                                                        <td>{asset.plot_size ? `${asset.plot_size}${asset.size_unit ? ` ${asset.size_unit}` : " m²"}` : "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Block Number:</td>
                                                                        <td>{asset.block_number || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Certificate of Title:</td>
                                                                        <td>{asset.certificate_of_title || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Tenure Years:</td>
                                                                        <td>{asset.tenure_years || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Rate Per Unit:</td>
                                                                        <td>{asset.rate_per_unit ? asset.rate_per_unit.toLocaleString() : "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Is Land Surveyed:</td>
                                                                        <td>
                                                                            <Badge color={asset.is_land_surveyed ? "success" : "secondary"}>
                                                                                {asset.is_land_surveyed ? "Yes" : "No"}
                                                                            </Badge>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Registration Number:</td>
                                                                        <td>{asset.registration_number || "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Right of Occupancy Start:</td>
                                                                        <td>{asset.right_of_occupance_start ? new Date(asset.right_of_occupance_start).toLocaleDateString() : "-"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="fw-bold">Right of Occupancy End:</td>
                                                                        <td>{asset.right_of_occupance_end ? new Date(asset.right_of_occupance_end).toLocaleDateString() : "-"}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Vehicle & Machinery Details */}
                                                        {(asset.registration_number || asset.chassis_number || asset.engine_number || asset.make || asset.model) && (
                                                            <div className="col-md-6 mb-3">
                                                                <h6 className="text-primary border-bottom pb-2 mb-2">Vehicle & Machinery Details</h6>
                                                                <table className="table table-sm table-borderless mb-0">
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="fw-bold" style={{ width: "40%" }}>Registration Number:</td>
                                                                            <td>{asset.registration_number || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Chassis Number:</td>
                                                                            <td>{asset.chassis_number || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Engine Number:</td>
                                                                            <td>{asset.engine_number || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Make:</td>
                                                                            <td>{asset.make || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Model:</td>
                                                                            <td>{asset.model || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Year of Manufacture:</td>
                                                                            <td>{asset.year_of_manufacture || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Engine Capacity:</td>
                                                                            <td>{asset.engine_capacity ? `${asset.engine_capacity} cc` : "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Fuel Type:</td>
                                                                            <td>{asset.fuel_type || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Imported From:</td>
                                                                            <td>{asset.imported_from || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Imported From Others:</td>
                                                                            <td>{asset.imported_from_others || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Body Type:</td>
                                                                            <td>{asset.body_type || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Body Type Others:</td>
                                                                            <td>{asset.body_type_others || "-"}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}

                                                        {/* Measurement */}
                                                        {(asset.measurement || asset.measurement_unit) && (
                                                            <div className="col-md-6 mb-3">
                                                                <h6 className="text-primary border-bottom pb-2 mb-2">Measurement</h6>
                                                                <table className="table table-sm table-borderless mb-0">
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="fw-bold" style={{ width: "40%" }}>Measurement:</td>
                                                                            <td>{asset.measurement || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Measurement Unit:</td>
                                                                            <td>{asset.measurement_unit || "-"}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}

                                                        {/* Valuation & Project */}
                                                        {(asset.client_name || asset.project_name) && (
                                                            <div className="col-md-6 mb-3">
                                                                <h6 className="text-primary border-bottom pb-2 mb-2">Valuation & Project</h6>
                                                                <table className="table table-sm table-borderless mb-0">
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="fw-bold" style={{ width: "40%" }}>Client Name:</td>
                                                                            <td>{asset.client_name || "-"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className="fw-bold">Project Name:</td>
                                                                            <td>{asset.project_name || "-"}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Collapse>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>

            {/* Photos Modal */}
            <Modal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                size="lg"
                centered
            >
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    Asset Photos
                </ModalHeader>
                <ModalBody>
                    {selectedPhotos && selectedPhotos.length > 0 ? (
                        <Carousel
                            activeIndex={activeIndex}
                            next={next}
                            previous={previous}
                            ride={false}
                        >
                            <CarouselIndicators
                                items={selectedPhotos}
                                activeIndex={activeIndex}
                                onClickHandler={goToIndex}
                            />

                            {selectedPhotos.map((photo) => (
                                <CarouselItem
                                    key={photo.id}
                                    onExiting={() => setAnimating(true)}
                                    onExited={() => setAnimating(false)}
                                >
                                    <img
                                        src={photo.photo_url}
                                        alt={photo.photo_type}
                                        className="d-block w-100 img-fluid"
                                        style={{ objectFit: "cover", height: "500px" }}
                                    />
                                    <CarouselCaption
                                        captionHeader={formatCaption(photo.photo_type)}
                                        headerClassName="text-white fw-bold"
                                        className="custom-caption"
                                    />
                                </CarouselItem>
                            ))}

                            {activeIndex > 0 && (
                                <CarouselControl
                                    direction="prev"
                                    directionText="Previous"
                                    onClickHandler={previous}
                                />
                            )}
                            {activeIndex < selectedPhotos.length - 1 && (
                                <CarouselControl
                                    direction="next"
                                    directionText="Next"
                                    onClickHandler={next}
                                />
                            )}
                        </Carousel>
                    ) : (
                        <Alert color="info">No photos available for this asset</Alert>
                    )}
                </ModalBody>
            </Modal>

            {/* Column Selector Modal */}
            <Modal 
                isOpen={columnModalOpen} 
                toggle={() => setColumnModalOpen(!columnModalOpen)}
                size="lg"
            >
                <ModalHeader toggle={() => setColumnModalOpen(!columnModalOpen)}>
                    <i className="ri-list-check me-2" style={{ color: BRAND_BLUE }}></i>
                    Select Columns to Export - All Assets
                </ModalHeader>
                <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <div className="mb-3 d-flex align-items-center justify-content-between">
                        <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={toggleAllColumns}
                            style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
                        >
                            <i className="ri-checkbox-multiple-line me-1"></i>
                            Select / Deselect All
                        </Button>
                        <small className="text-muted">
                            {getExportHeaders().length} columns available
                        </small>
                    </div>

                    <Row>
                        {getExportHeaders().map((header, index) => {
                            const checkboxId = `column-${header.key}-${index}`;
                            const isDefaultSelected =
                                header.defaultSelected === undefined
                                    ? true
                                    : header.defaultSelected;
                            return (
                                <Col md={6} lg={4} key={checkboxId} className="mb-2">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            id={checkboxId}
                                            className="form-check-input"
                                            name="export-column"
                                            defaultChecked={isDefaultSelected}
                                            style={{ cursor: "pointer" }}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={checkboxId}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            {header.label}
                                        </label>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="secondary" 
                        onClick={() => setColumnModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={performExport}
                        style={{ 
                            backgroundColor: BRAND_BLUE, 
                            borderColor: BRAND_BLUE,
                        }}
                    >
                        <i className="ri-download-line me-2"></i>
                        Export {pendingExportType?.toUpperCase()}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default AssetsTab;
